import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { Save, Mail, Clock, Settings as SettingsIcon, Calendar, Users, Phone, GripVertical, ArrowUp, ArrowDown, Bell, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  
  const [restaurantSettings, setRestaurantSettings] = useState({
    name: "BARBAAR Middelkerke",
    email: "reservatie@barbaarmiddelkerke.com",
    phone: "+32 123 456 789",
    contact_phone: "0471556751",  // Phone for customers to call
    address: "Middelkerke, België",
    default_capacity: 50,
    booking_buffer_minutes: 15,
    max_party_size: 12,
    min_advance_hours: 2,
    max_advance_days: 60,
    cancellation_hours: 24,
    max_guests_per_15min: 20,
    max_table_occupation_minutes: 150  // 2h30 default
  });

  const [openingHours, setOpeningHours] = useState({
    monday: { open: true, lunch: "11:30-15:00", dinner: "17:30-23:00" },
    tuesday: { open: true, lunch: "11:30-15:00", dinner: "17:30-23:00" },
    wednesday: { open: false, lunch: "", dinner: "" },
    thursday: { open: true, lunch: "11:30-15:00", dinner: "17:30-23:00" },
    friday: { open: true, lunch: "11:30-15:00", dinner: "17:30-23:00" },
    saturday: { open: true, lunch: "11:30-15:00", dinner: "17:30-23:00" },
    sunday: { open: true, lunch: "11:30-15:00", dinner: "17:30-22:00" }
  });

  const [emailTemplates, setEmailTemplates] = useState({
    confirmation_nl: {
      subject: "Uw reservering bij BARBAAR is bevestigd",
      body: `Beste {{guest_name}},\n\nUw reservering bij BARBAAR Middelkerke is bevestigd.\n\nDatum: {{date}}\nTijd: {{time}}\nAantal personen: {{party_size}}\n\nSpeciale verzoeken: {{special_requests}}\nAllergieën: {{allergies}}\n\nBeheer uw reservering: {{manage_url}}\n\nWe kijken ernaar uit u te verwelkomen!\n\nMet vriendelijke groet,\nTeam BARBAAR`
    },
    confirmation_en: {
      subject: "Your reservation at BARBAAR is confirmed",
      body: `Dear {{guest_name}},\n\nYour reservation at BARBAAR Middelkerke has been confirmed.\n\nDate: {{date}}\nTime: {{time}}\nParty size: {{party_size}}\n\nSpecial requests: {{special_requests}}\nAllergies: {{allergies}}\n\nManage your reservation: {{manage_url}}\n\nWe look forward to welcoming you!\n\nBest regards,\nTeam BARBAAR`
    },
    reminder_nl: {
      subject: "Herinnering: Uw reservering bij BARBAAR morgen",
      body: `Beste {{guest_name}},\n\nDit is een herinnering voor uw reservering morgen bij BARBAAR Middelkerke.\n\nDatum: {{date}}\nTijd: {{time}}\nAantal personen: {{party_size}}\n\nTot morgen!`
    },
    reminder_en: {
      subject: "Reminder: Your reservation at BARBAAR tomorrow",
      body: `Dear {{guest_name}},\n\nThis is a reminder for your reservation tomorrow at BARBAAR Middelkerke.\n\nDate: {{date}}\nTime: {{time}}\nParty size: {{party_size}}\n\nSee you tomorrow!`
    },
    feedback_nl: {
      subject: "Bedankt voor uw bezoek aan BARBAAR!",
      body: `Beste {{guest_name}},\n\nBedankt voor uw bezoek aan BARBAAR Middelkerke. We hopen dat u een heerlijke ervaring heeft gehad!\n\nWe zouden het zeer waarderen als u uw ervaring met anderen wilt delen door een review achter te laten.\n\nUw feedback helpt ons verbeteren en helpt andere gasten BARBAAR ontdekken!\n\nMet warme groet,\nTeam BARBAAR`
    },
    feedback_en: {
      subject: "Thank you for visiting BARBAAR!",
      body: `Dear {{guest_name}},\n\nThank you for dining with us at BARBAAR Middelkerke. We hope you had a wonderful experience!\n\nWe would be very grateful if you could share your experience with others by leaving a review.\n\nYour feedback helps us improve and helps other guests discover BARBAAR!\n\nWith warm regards,\nTeam BARBAAR`
    },
    google_review_url: "https://g.page/r/CZ6M6YD-zvGhEAE/review",
    // Email styling options
    email_text_color: "#F4F0EA",  // Light beige for dark background
    email_background_color: "#121212",
    email_accent_color: "#D4AF37"  // Gold
  });

  const [recurringShifts, setRecurringShifts] = useState([
    {
      id: "lunch-weekday",
      name: "Lunch Service (Weekdays)",
      days: ["monday", "tuesday", "thursday", "friday", "saturday"],
      start_time: "11:30",
      end_time: "15:00",
      slots: "11:30,12:00,12:30,13:00,13:30,14:00",
      capacity_per_slot: 8,
      active: true
    },
    {
      id: "dinner-weekday",
      name: "Dinner Service (Weekdays)",
      days: ["monday", "tuesday", "thursday", "friday"],
      start_time: "17:30",
      end_time: "23:00",
      slots: "17:30,18:00,18:30,19:00,19:30,20:00,20:30,21:00",
      capacity_per_slot: 10,
      active: true
    },
    {
      id: "dinner-weekend",
      name: "Dinner Service (Weekend)",
      days: ["saturday", "sunday"],
      start_time: "17:30",
      end_time: "23:00",
      slots: "17:30,18:00,18:30,19:00,19:30,20:00,20:30,21:00,21:30",
      capacity_per_slot: 12,
      active: true
    }
  ]);

  // Seasonal closing schedules
  const [closingSchedules, setClosingSchedules] = useState([
    {
      id: "default",
      name: "Standaard (Laagseizoen)",
      start_date: "",
      end_date: "",
      is_default: true,
      closed_days: ["wednesday", "thursday"] // Gesloten op woensdag en donderdag
    }
  ]);

  // Seasonal opening hours schedules
  const [openingHoursSchedules, setOpeningHoursSchedules] = useState([
    {
      id: "default",
      name: "Standaard Openingsuren",
      start_date: "",
      end_date: "",
      is_default: true,
      hours: {
        monday: { open: true, lunch: "11:30-15:00", dinner: "17:30-22:00" },
        tuesday: { open: true, lunch: "11:30-15:00", dinner: "17:30-22:00" },
        wednesday: { open: false, lunch: "", dinner: "" },
        thursday: { open: false, lunch: "", dinner: "" },
        friday: { open: true, lunch: "11:30-15:00", dinner: "17:30-23:00" },
        saturday: { open: true, lunch: "11:30-15:00", dinner: "17:30-23:00" },
        sunday: { open: true, lunch: "11:30-15:00", dinner: "17:30-22:00" }
      }
    }
  ]);

  // Specific closed dates (holidays, vacation, etc.)
  const [closedDates, setClosedDates] = useState([]);
  const [newClosedDate, setNewClosedDate] = useState({ date: "", reason: "" });

  // Table group presets
  const [tableGroupPresets, setTableGroupPresets] = useState([]);
  const [allTables, setAllTables] = useState([]);
  const [newPreset, setNewPreset] = useState({ name: "", party_size: 4, table_ids: [] });

  useEffect(() => {
    loadSettings();
    loadTables();
  }, []);

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`);
      // Filter out decor elements
      const tables = response.data.filter(t => t.type !== 'decor');
      setAllTables(tables);
    } catch (error) {
      console.log("Error loading tables:", error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data) {
        setSettings(response.data);
        if (response.data.restaurant) {
          setRestaurantSettings({
            ...restaurantSettings,
            ...response.data.restaurant
          });
        }
        if (response.data.opening_hours) setOpeningHours(response.data.opening_hours);
        // Merge email templates with defaults to ensure all required fields exist
        if (response.data.email_templates) {
          setEmailTemplates(prev => ({
            ...prev,
            ...response.data.email_templates,
            confirmation_nl: { ...prev.confirmation_nl, ...response.data.email_templates.confirmation_nl },
            confirmation_en: { ...prev.confirmation_en, ...response.data.email_templates.confirmation_en },
            reminder_nl: { ...prev.reminder_nl, ...response.data.email_templates.reminder_nl },
            reminder_en: { ...prev.reminder_en, ...response.data.email_templates.reminder_en },
            feedback_nl: { ...prev.feedback_nl, ...response.data.email_templates.feedback_nl },
            feedback_en: { ...prev.feedback_en, ...response.data.email_templates.feedback_en }
          }));
        }
        if (response.data.recurring_shifts) setRecurringShifts(response.data.recurring_shifts);
        if (response.data.closing_schedules) setClosingSchedules(response.data.closing_schedules);
        if (response.data.closed_dates) setClosedDates(response.data.closed_dates);
        if (response.data.table_group_presets) setTableGroupPresets(response.data.table_group_presets);
        if (response.data.opening_hours_schedules) setOpeningHoursSchedules(response.data.opening_hours_schedules);
      }
    } catch (error) {
      console.log("No settings found, using defaults");
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/settings`, {
        restaurant: restaurantSettings,
        opening_hours: openingHours,
        email_templates: emailTemplates,
        recurring_shifts: recurringShifts,
        closing_schedules: closingSchedules,
        closed_dates: closedDates,
        table_group_presets: tableGroupPresets,
        opening_hours_schedules: openingHoursSchedules
      });
      toast.success("Instellingen opgeslagen");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Fout bij opslaan instellingen");
    } finally {
      setLoading(false);
    }
  };

  const addClosedDate = () => {
    if (!newClosedDate.date) {
      toast.error("Selecteer een datum");
      return;
    }
    setClosedDates([...closedDates, { ...newClosedDate, id: Date.now().toString() }]);
    setNewClosedDate({ date: "", reason: "" });
    toast.success("Sluitingsdag toegevoegd");
  };

  const removeClosedDate = (id) => {
    setClosedDates(closedDates.filter(d => d.id !== id));
  };

  const addClosingSchedule = () => {
    setClosingSchedules([...closingSchedules, {
      id: Date.now().toString(),
      name: "Nieuw Seizoen",
      start_date: "",
      end_date: "",
      is_default: false,
      closed_days: []
    }]);
  };

  const updateClosingSchedule = (id, updates) => {
    setClosingSchedules(closingSchedules.map(s => 
      s.id === id ? { ...s, ...updates } : s
    ));
  };

  const removeClosingSchedule = (id) => {
    setClosingSchedules(closingSchedules.filter(s => s.id !== id));
  };

  const toggleClosedDay = (scheduleId, day) => {
    setClosingSchedules(closingSchedules.map(s => {
      if (s.id === scheduleId) {
        const newDays = s.closed_days.includes(day)
          ? s.closed_days.filter(d => d !== day)
          : [...s.closed_days, day];
        return { ...s, closed_days: newDays };
      }
      return s;
    }));
  };

  const dayNames = {
    monday: "Maandag",
    tuesday: "Dinsdag",
    wednesday: "Woensdag",
    thursday: "Donderdag",
    friday: "Vrijdag",
    saturday: "Zaterdag",
    sunday: "Zondag"
  };

  // Opening hours schedules functions
  const addOpeningHoursSchedule = () => {
    setOpeningHoursSchedules([...openingHoursSchedules, {
      id: Date.now().toString(),
      name: "Nieuw Seizoen",
      start_date: "",
      end_date: "",
      is_default: false,
      hours: {
        monday: { open: true, lunch: "11:30-15:00", dinner: "17:30-22:00" },
        tuesday: { open: true, lunch: "11:30-15:00", dinner: "17:30-22:00" },
        wednesday: { open: true, lunch: "11:30-15:00", dinner: "17:30-22:00" },
        thursday: { open: true, lunch: "11:30-15:00", dinner: "17:30-22:00" },
        friday: { open: true, lunch: "11:30-15:00", dinner: "17:30-23:00" },
        saturday: { open: true, lunch: "11:30-15:00", dinner: "17:30-23:00" },
        sunday: { open: true, lunch: "11:30-15:00", dinner: "17:30-22:00" }
      }
    }]);
  };

  const updateOpeningHoursSchedule = (id, updates) => {
    setOpeningHoursSchedules(openingHoursSchedules.map(s => 
      s.id === id ? { ...s, ...updates } : s
    ));
  };

  const removeOpeningHoursSchedule = (id) => {
    // Don't remove the default schedule
    const schedule = openingHoursSchedules.find(s => s.id === id);
    if (schedule?.is_default) {
      toast.error("Het standaard schema kan niet verwijderd worden");
      return;
    }
    setOpeningHoursSchedules(openingHoursSchedules.filter(s => s.id !== id));
  };

  const updateScheduleHours = (scheduleId, day, field, value) => {
    setOpeningHoursSchedules(openingHoursSchedules.map(s => {
      if (s.id === scheduleId) {
        return {
          ...s,
          hours: {
            ...s.hours,
            [day]: { ...s.hours[day], [field]: value }
          }
        };
      }
      return s;
    }));
  };

  // Table group preset functions
  const addTableGroupPreset = () => {
    if (!newPreset.name || newPreset.table_ids.length === 0) {
      toast.error("Geef een naam en selecteer minstens één tafel");
      return;
    }
    // Add with priority (order in list)
    const newPriority = tableGroupPresets.length;
    setTableGroupPresets([...tableGroupPresets, { 
      ...newPreset, 
      id: Date.now().toString(),
      priority: newPriority
    }]);
    setNewPreset({ name: "", party_size: 4, table_ids: [] });
    toast.success("Tafelgroep toegevoegd");
  };

  const removeTableGroupPreset = (id) => {
    const filtered = tableGroupPresets.filter(p => p.id !== id);
    // Re-assign priorities
    const reordered = filtered.map((p, idx) => ({ ...p, priority: idx }));
    setTableGroupPresets(reordered);
  };

  const movePreset = (id, direction) => {
    const sorted = [...tableGroupPresets].sort((a, b) => (a.priority || 0) - (b.priority || 0));
    const idx = sorted.findIndex(p => p.id === id);
    if (idx === -1) return;
    
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= sorted.length) return;
    
    // Swap priorities
    const temp = sorted[idx];
    sorted[idx] = sorted[newIdx];
    sorted[newIdx] = temp;
    
    // Re-assign priorities
    const reordered = sorted.map((p, i) => ({ ...p, priority: i }));
    setTableGroupPresets(reordered);
  };

  const toggleTableInPreset = (tableId) => {
    setNewPreset(prev => ({
      ...prev,
      table_ids: prev.table_ids.includes(tableId)
        ? prev.table_ids.filter(id => id !== tableId)
        : [...prev.table_ids, tableId]
    }));
  };

  const getTableLabel = (tableId) => {
    const table = allTables.find(t => t.id === tableId);
    if (!table) return tableId;
    if (table.type === 'bar') return `B${table.number}`;
    return `T${table.number}`;
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="settings-view">
      <div className="p-6 border-b border-white/10 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-serif text-gray-300" data-testid="settings-title">Instellingen</h2>
          <p className="text-sm text-gray-300">Configureer uw restaurant en systeem instellingen</p>
        </div>
        <Button 
          onClick={saveSettings}
          disabled={loading}
          className="bg-brand-gold text-gray-300 hover:bg-[#F0C74A]"
          data-testid="save-settings-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? "Opslaan..." : "Opslaan"}
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <Tabs defaultValue="restaurant" className="w-full">
          <TabsList className="grid w-full grid-cols-8 bg-[#1A1A1A]">
            <TabsTrigger value="restaurant" data-testid="tab-restaurant">
              <SettingsIcon className="w-4 h-4 mr-2" />
              Restaurant
            </TabsTrigger>
            <TabsTrigger value="hours" data-testid="tab-hours">
              <Clock className="w-4 h-4 mr-2" />
              Openingsuren
            </TabsTrigger>
            <TabsTrigger value="closings" data-testid="tab-closings">
              <Calendar className="w-4 h-4 mr-2" />
              Sluitingsdagen
            </TabsTrigger>
            <TabsTrigger value="tablegroups" data-testid="tab-tablegroups">
              <Users className="w-4 h-4 mr-2" />
              Tafelgroepen
            </TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-reminders">
              <Bell className="w-4 h-4 mr-2" />
              Herinneringen
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">
              <Star className="w-4 h-4 mr-2" />
              Reviews
            </TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="shifts" data-testid="tab-shifts">
              <Clock className="w-4 h-4 mr-2" />
              Shifts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="restaurant" className="mt-6">
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardHeader>
                <CardTitle className="text-brand-beige">Restaurant Informatie</CardTitle>
                <CardDescription>Basis informatie over uw restaurant</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300 font-medium">Restaurant Naam</Label>
                    <Input
                      value={restaurantSettings.name}
                      onChange={(e) => setRestaurantSettings({...restaurantSettings, name: e.target.value})}
                      className="bg-[#222222] border-white/20"
                      data-testid="input-restaurant-name"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium">Email</Label>
                    <Input
                      type="email"
                      value={restaurantSettings.email}
                      onChange={(e) => setRestaurantSettings({...restaurantSettings, email: e.target.value})}
                      className="bg-[#222222] border-white/20"
                      data-testid="input-restaurant-email"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium">Telefoonnummer (intern)</Label>
                    <Input
                      value={restaurantSettings.phone}
                      onChange={(e) => setRestaurantSettings({...restaurantSettings, phone: e.target.value})}
                      className="bg-[#222222] border-white/20"
                      data-testid="input-restaurant-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4 text-brand-gold" />
                      Contact Telefoonnummer (voor klanten)
                    </Label>
                    <Input
                      value={restaurantSettings.contact_phone}
                      onChange={(e) => setRestaurantSettings({...restaurantSettings, contact_phone: e.target.value})}
                      className="bg-[#222222] border-white/20"
                      placeholder="0471556751"
                      data-testid="input-contact-phone"
                    />
                    <p className="text-xs text-gray-500 mt-1">Dit nummer wordt getoond wanneer het restaurant vol is of bij last-minute boekingen</p>
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium">Adres</Label>
                    <Input
                      value={restaurantSettings.address}
                      onChange={(e) => setRestaurantSettings({...restaurantSettings, address: e.target.value})}
                      className="bg-[#222222] border-white/20"
                      data-testid="input-restaurant-address"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <h3 className="text-lg font-serif text-gray-300 mb-4">Reserveringsinstellingen</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-300 font-medium">Standaard Capaciteit</Label>
                      <Input
                        type="number"
                        value={restaurantSettings.default_capacity}
                        onChange={(e) => setRestaurantSettings({...restaurantSettings, default_capacity: parseInt(e.target.value)})}
                        className="bg-[#222222] border-white/20"
                        data-testid="input-default-capacity"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 font-medium">Buffer tussen reserveringen (min)</Label>
                      <Input
                        type="number"
                        value={restaurantSettings.booking_buffer_minutes}
                        onChange={(e) => setRestaurantSettings({...restaurantSettings, booking_buffer_minutes: parseInt(e.target.value)})}
                        className="bg-[#222222] border-white/20"
                        data-testid="input-buffer-minutes"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 font-medium">Max Groepsgrootte</Label>
                      <Input
                        type="number"
                        value={restaurantSettings.max_party_size}
                        onChange={(e) => setRestaurantSettings({...restaurantSettings, max_party_size: parseInt(e.target.value)})}
                        className="bg-[#222222] border-white/20"
                        data-testid="input-max-party-size"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 font-medium">Min. vooraf boeken (uren)</Label>
                      <Input
                        type="number"
                        value={restaurantSettings.min_advance_hours}
                        onChange={(e) => setRestaurantSettings({...restaurantSettings, min_advance_hours: parseInt(e.target.value)})}
                        className="bg-[#222222] border-white/20"
                        data-testid="input-min-advance"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 font-medium">Max. vooraf boeken (dagen)</Label>
                      <Input
                        type="number"
                        value={restaurantSettings.max_advance_days}
                        onChange={(e) => setRestaurantSettings({...restaurantSettings, max_advance_days: parseInt(e.target.value)})}
                        className="bg-[#222222] border-white/20"
                        data-testid="input-max-advance"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 font-medium">Annulering voor (uren)</Label>
                      <Input
                        type="number"
                        value={restaurantSettings.cancellation_hours}
                        onChange={(e) => setRestaurantSettings({...restaurantSettings, cancellation_hours: parseInt(e.target.value)})}
                        className="bg-[#222222] border-white/20"
                        data-testid="input-cancellation-hours"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 font-medium">Max. gasten per 15 min</Label>
                      <Input
                        type="number"
                        value={restaurantSettings.max_guests_per_15min}
                        onChange={(e) => setRestaurantSettings({...restaurantSettings, max_guests_per_15min: parseInt(e.target.value)})}
                        className="bg-[#222222] border-white/20"
                        data-testid="input-max-guests-15min"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 font-medium flex items-center gap-2">
                        <Clock className="w-4 h-4 text-brand-gold" />
                        Max. Tafelbezetting (minuten)
                      </Label>
                      <Input
                        type="number"
                        value={restaurantSettings.max_table_occupation_minutes || 150}
                        onChange={(e) => setRestaurantSettings({...restaurantSettings, max_table_occupation_minutes: parseInt(e.target.value)})}
                        className="bg-[#222222] border-white/20"
                        placeholder="150"
                        data-testid="input-max-occupation"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Bijv. 150 = 2u30. Deze tijd wordt gebruikt voor het plannen van meerdere reservaties op dezelfde tafel.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hours" className="mt-6 space-y-6">
            {/* Info card */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-blue-400 text-sm">
                <strong>Seizoensgebonden Openingsuren:</strong> Stel per periode verschillende openingsuren in. 
                Het systeem gebruikt automatisch het juiste schema op basis van de datum. 
                Periodes met een specifieke datumrange hebben voorrang op het standaard schema.
              </p>
            </div>

            {/* Opening hours schedules */}
            {openingHoursSchedules.map((schedule, idx) => (
              <Card key={schedule.id} className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Input
                        value={schedule.name}
                        onChange={(e) => updateOpeningHoursSchedule(schedule.id, { name: e.target.value })}
                        className="bg-[#222222] border-white/20 text-lg font-semibold"
                        placeholder="Naam schema"
                        data-testid={`hours-schedule-name-${idx}`}
                      />
                    </div>
                    {!schedule.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOpeningHoursSchedule(schedule.id)}
                        className="text-red-400 hover:text-red-300 ml-4"
                      >
                        Verwijderen
                      </Button>
                    )}
                  </div>
                  {schedule.is_default ? (
                    <CardDescription className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-brand-gold/20 text-brand-gold text-xs rounded">STANDAARD</span>
                      Wordt gebruikt wanneer geen ander schema van toepassing is
                    </CardDescription>
                  ) : (
                    <div className="flex items-center gap-4 mt-2">
                      <div>
                        <Label className="text-xs text-gray-500">Van datum</Label>
                        <Input
                          type="date"
                          value={schedule.start_date || ""}
                          onChange={(e) => updateOpeningHoursSchedule(schedule.id, { start_date: e.target.value })}
                          className="bg-[#222222] border-white/20"
                          data-testid={`hours-schedule-start-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Tot datum</Label>
                        <Input
                          type="date"
                          value={schedule.end_date || ""}
                          onChange={(e) => updateOpeningHoursSchedule(schedule.id, { end_date: e.target.value })}
                          className="bg-[#222222] border-white/20"
                          data-testid={`hours-schedule-end-${idx}`}
                        />
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.keys(dayNames).map((day) => (
                    <div key={day} className="flex items-center gap-4 p-3 bg-[#222222] rounded-lg">
                      <div className="w-28">
                        <span className="text-gray-300 font-medium">{dayNames[day]}</span>
                      </div>
                      <Switch
                        checked={schedule.hours?.[day]?.open ?? true}
                        onCheckedChange={(checked) => updateScheduleHours(schedule.id, day, 'open', checked)}
                        data-testid={`hours-${schedule.id}-${day}-open`}
                      />
                      {schedule.hours?.[day]?.open && (
                        <>
                          <div className="flex-1">
                            <Label className="text-xs text-gray-500">Lunch</Label>
                            <Input
                              value={schedule.hours?.[day]?.lunch || ""}
                              onChange={(e) => updateScheduleHours(schedule.id, day, 'lunch', e.target.value)}
                              placeholder="11:30-15:00"
                              className="bg-[#1A1A1A] border-white/20 text-sm"
                              data-testid={`hours-${schedule.id}-${day}-lunch`}
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-xs text-gray-500">Diner</Label>
                            <Input
                              value={schedule.hours?.[day]?.dinner || ""}
                              onChange={(e) => updateScheduleHours(schedule.id, day, 'dinner', e.target.value)}
                              placeholder="17:30-23:00"
                              className="bg-[#1A1A1A] border-white/20 text-sm"
                              data-testid={`hours-${schedule.id}-${day}-dinner`}
                            />
                          </div>
                        </>
                      )}
                      {!schedule.hours?.[day]?.open && (
                        <span className="text-red-400 text-sm">Gesloten</span>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}

            {/* Add new schedule button */}
            <Button
              onClick={addOpeningHoursSchedule}
              variant="outline"
              className="w-full border-dashed border-white/20 text-gray-400 hover:text-white hover:border-white/40"
              data-testid="add-hours-schedule-btn"
            >
              + Nieuw Openingsuren Schema Toevoegen (bijv. Vakantie, Hoogseizoen)
            </Button>
          </TabsContent>

          <TabsContent value="closings" className="mt-6 space-y-6">
            {/* Seasonal Closing Schedules */}
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardHeader>
                <CardTitle className="text-brand-beige">Seizoensgebonden Sluitingsdagen</CardTitle>
                <CardDescription>Stel per seizoen/periode in welke dagen u gesloten bent</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {closingSchedules.map((schedule, idx) => (
                  <div key={schedule.id} className="p-4 bg-[#222222] rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Input
                        value={schedule.name}
                        onChange={(e) => updateClosingSchedule(schedule.id, { name: e.target.value })}
                        className="bg-transparent border-0 text-gray-300 font-semibold p-0 h-auto text-lg focus:ring-0 max-w-[200px]"
                        placeholder="Naam periode"
                      />
                      {!schedule.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeClosingSchedule(schedule.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          Verwijderen
                        </Button>
                      )}
                    </div>
                    
                    {!schedule.is_default && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-gray-500">Van datum</Label>
                          <Input
                            type="date"
                            value={schedule.start_date}
                            onChange={(e) => updateClosingSchedule(schedule.id, { start_date: e.target.value })}
                            className="bg-[#1A1A1A] border-white/20"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Tot datum</Label>
                          <Input
                            type="date"
                            value={schedule.end_date}
                            onChange={(e) => updateClosingSchedule(schedule.id, { end_date: e.target.value })}
                            className="bg-[#1A1A1A] border-white/20"
                          />
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-xs text-gray-500 mb-2 block">
                        {schedule.is_default ? "Standaard sluitingsdagen (als geen ander seizoen actief)" : "Gesloten op:"}
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(dayNames).map(([key, name]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => toggleClosedDay(schedule.id, key)}
                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                              schedule.closed_days.includes(key) 
                                ? 'bg-red-500/30 text-red-300 border border-red-500/50' 
                                : 'bg-[#1A1A1A] text-gray-400 border border-white/10 hover:border-white/20'
                            }`}
                          >
                            {name}
                          </button>
                        ))}
                      </div>
                      {schedule.closed_days.length === 0 && (
                        <p className="text-xs text-green-400 mt-2">✓ 7 dagen per week open</p>
                      )}
                    </div>
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  onClick={addClosingSchedule}
                  className="w-full border-dashed border-white/20 text-gray-400 hover:text-white"
                >
                  + Nieuw Seizoen Toevoegen
                </Button>
              </CardContent>
            </Card>

            {/* Specific Closed Dates */}
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardHeader>
                <CardTitle className="text-brand-beige">Specifieke Sluitingsdagen</CardTitle>
                <CardDescription>Voeg specifieke datums toe waarop u gesloten bent (feestdagen, verlof, etc.)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      type="date"
                      value={newClosedDate.date}
                      onChange={(e) => setNewClosedDate({ ...newClosedDate, date: e.target.value })}
                      className="bg-[#222222] border-white/20"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Reden (optioneel)"
                      value={newClosedDate.reason}
                      onChange={(e) => setNewClosedDate({ ...newClosedDate, reason: e.target.value })}
                      className="bg-[#222222] border-white/20"
                    />
                  </div>
                  <Button onClick={addClosedDate} className="bg-brand-gold text-black hover:bg-[#F0C74A]">
                    Toevoegen
                  </Button>
                </div>
                
                {closedDates.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">Geen specifieke sluitingsdagen ingesteld</p>
                ) : (
                  <div className="space-y-2">
                    {closedDates.sort((a, b) => a.date.localeCompare(b.date)).map((cd) => (
                      <div key={cd.id} className="flex items-center justify-between p-3 bg-[#222222] rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-red-400 font-semibold">
                            {new Date(cd.date + 'T12:00:00').toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                          {cd.reason && <span className="text-gray-500">- {cd.reason}</span>}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeClosedDate(cd.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Table Group Presets Tab */}
          <TabsContent value="tablegroups" className="mt-6">
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardHeader>
                <CardTitle className="text-brand-beige">Tafelgroep Combinaties</CardTitle>
                <CardDescription>Definieer vaste tafelcombinaties per groepsgrootte. Deze worden automatisch gebruikt bij tafeltoewijzing.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add new preset */}
                <div className="p-4 bg-[#222222] rounded-lg space-y-4">
                  <h4 className="text-gray-300 font-semibold">Nieuwe Tafelgroep Toevoegen</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Naam</Label>
                      <Input
                        value={newPreset.name}
                        onChange={(e) => setNewPreset({...newPreset, name: e.target.value})}
                        placeholder="bijv. Groep 4 personen"
                        className="bg-[#1A1A1A] border-white/20"
                        data-testid="input-preset-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Groepsgrootte (personen)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newPreset.party_size}
                        onChange={(e) => setNewPreset({...newPreset, party_size: parseInt(e.target.value) || 1})}
                        className="bg-[#1A1A1A] border-white/20"
                        data-testid="input-preset-size"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-2 block">Selecteer Tafels</Label>
                    <div className="flex flex-wrap gap-2">
                      {allTables.sort((a, b) => a.number - b.number).map((table) => (
                        <button
                          key={table.id}
                          type="button"
                          onClick={() => toggleTableInPreset(table.id)}
                          className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                            newPreset.table_ids.includes(table.id)
                              ? 'bg-brand-gold text-black font-semibold'
                              : 'bg-[#1A1A1A] text-gray-400 border border-white/10 hover:border-white/20'
                          }`}
                          data-testid={`preset-table-${table.id}`}
                        >
                          {table.type === 'bar' ? `B${table.number}` : `T${table.number}`} ({table.capacity}p)
                        </button>
                      ))}
                    </div>
                    {newPreset.table_ids.length > 0 && (
                      <p className="text-xs text-brand-gold mt-2">
                        Geselecteerd: {newPreset.table_ids.map(id => getTableLabel(id)).join(' + ')} 
                        ({allTables.filter(t => newPreset.table_ids.includes(t.id)).reduce((sum, t) => sum + t.capacity, 0)} totale capaciteit)
                      </p>
                    )}
                  </div>
                  <Button 
                    onClick={addTableGroupPreset} 
                    className="bg-brand-gold text-black hover:bg-[#F0C74A]"
                    data-testid="add-preset-button"
                  >
                    Tafelgroep Toevoegen
                  </Button>
                </div>

                {/* Existing presets */}
                {tableGroupPresets.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">Geen tafelgroepen ingesteld</p>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-gray-300 font-semibold">Bestaande Tafelgroepen</h4>
                      <p className="text-xs text-gray-500">Sleep of gebruik pijlen om volgorde aan te passen (eerste = hoogste prioriteit)</p>
                    </div>
                    {[...tableGroupPresets].sort((a, b) => (a.priority || 0) - (b.priority || 0)).map((preset, idx, arr) => (
                      <div key={preset.id} className="flex items-center gap-2 p-4 bg-[#222222] rounded-lg">
                        {/* Priority indicator */}
                        <div className="flex flex-col items-center gap-1 mr-2">
                          <button
                            onClick={() => movePreset(preset.id, 'up')}
                            disabled={idx === 0}
                            className={`p-1 rounded ${idx === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:text-white hover:bg-white/10'}`}
                            data-testid={`preset-up-${preset.id}`}
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                          <span className="text-xs text-gray-500 font-mono">{idx + 1}</span>
                          <button
                            onClick={() => movePreset(preset.id, 'down')}
                            disabled={idx === arr.length - 1}
                            className={`p-1 rounded ${idx === arr.length - 1 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:text-white hover:bg-white/10'}`}
                            data-testid={`preset-down-${preset.id}`}
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Preset info */}
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center">
                            <span className="text-brand-gold font-bold">{preset.party_size}p</span>
                          </div>
                          <div>
                            <p className="text-gray-300 font-semibold">{preset.name}</p>
                            <p className="text-sm text-gray-500">
                              Tafels: {preset.table_ids.map(id => getTableLabel(id)).join(' + ')}
                            </p>
                          </div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTableGroupPreset(preset.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`remove-preset-${preset.id}`}
                        >
                          Verwijderen
                        </Button>
                      </div>
                    ))}
                    <p className="text-xs text-amber-500 bg-amber-500/10 p-2 rounded">
                      Tip: Plaats tafels aan de bar onderaan zodat deze het laatste worden bezet
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reminders Tab */}
          <TabsContent value="reminders" className="mt-6">
            <div className="space-y-6">
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-brand-beige flex items-center gap-2">
                    <Bell className="w-5 h-5 text-brand-gold" />
                    Herinnering Email (Nederlands)
                  </CardTitle>
                  <CardDescription>
                    Deze email wordt automatisch 24 uur voor de reservering verstuurd.
                    <br />
                    Variabelen: {"{{guest_name}}"}, {"{{date}}"}, {"{{time}}"}, {"{{party_size}}"}, {"{{manage_url}}"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-gray-300 font-medium">Onderwerp</Label>
                    <Input
                      value={emailTemplates?.reminder_nl?.subject || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        reminder_nl: { ...emailTemplates?.reminder_nl, subject: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20"
                      data-testid="input-reminder-nl-subject"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium">Email Inhoud</Label>
                    <Textarea
                      value={emailTemplates?.reminder_nl?.body || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        reminder_nl: { ...emailTemplates?.reminder_nl, body: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20 min-h-[200px] font-mono text-sm"
                      placeholder="Beste {{guest_name}},&#10;&#10;Dit is een herinnering..."
                      data-testid="textarea-reminder-nl-body"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-brand-beige flex items-center gap-2">
                    <Bell className="w-5 h-5 text-brand-gold" />
                    Reminder Email (English)
                  </CardTitle>
                  <CardDescription>
                    This email is automatically sent 24 hours before the reservation.
                    <br />
                    Variables: {"{{guest_name}}"}, {"{{date}}"}, {"{{time}}"}, {"{{party_size}}"}, {"{{manage_url}}"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-gray-300 font-medium">Subject</Label>
                    <Input
                      value={emailTemplates?.reminder_en?.subject || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        reminder_en: { ...emailTemplates?.reminder_en, subject: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20"
                      data-testid="input-reminder-en-subject"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium">Email Body</Label>
                    <Textarea
                      value={emailTemplates?.reminder_en?.body || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        reminder_en: { ...emailTemplates?.reminder_en, body: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20 min-h-[200px] font-mono text-sm"
                      placeholder="Dear {{guest_name}},&#10;&#10;This is a reminder..."
                      data-testid="textarea-reminder-en-body"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-blue-400 text-sm">
                  <strong>Automatische verzending:</strong> Herinneringen worden dagelijks automatisch verstuurd voor alle reserveringen van de volgende dag. U hoeft hier niets voor te doen.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="mt-6">
            <div className="space-y-6">
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-brand-beige flex items-center gap-2">
                    <Star className="w-5 h-5 text-brand-gold" />
                    Review Email (Nederlands)
                  </CardTitle>
                  <CardDescription>
                    Deze email wordt automatisch 24 uur na het bezoek verstuurd om een review te vragen.
                    <br />
                    Variabelen: {"{{guest_name}}"}, {"{{review_url}}"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-gray-300 font-medium">Onderwerp</Label>
                    <Input
                      value={emailTemplates?.feedback_nl?.subject || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        feedback_nl: { ...emailTemplates?.feedback_nl, subject: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20"
                      data-testid="input-feedback-nl-subject"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium">Email Inhoud</Label>
                    <Textarea
                      value={emailTemplates?.feedback_nl?.body || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        feedback_nl: { ...emailTemplates?.feedback_nl, body: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20 min-h-[200px] font-mono text-sm"
                      placeholder="Beste {{guest_name}},&#10;&#10;Bedankt voor uw bezoek..."
                      data-testid="textarea-feedback-nl-body"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-brand-beige flex items-center gap-2">
                    <Star className="w-5 h-5 text-brand-gold" />
                    Review Email (English)
                  </CardTitle>
                  <CardDescription>
                    This email is automatically sent 24 hours after the visit to request a review.
                    <br />
                    Variables: {"{{guest_name}}"}, {"{{review_url}}"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-gray-300 font-medium">Subject</Label>
                    <Input
                      value={emailTemplates?.feedback_en?.subject || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        feedback_en: { ...emailTemplates?.feedback_en, subject: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20"
                      data-testid="input-feedback-en-subject"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium">Email Body</Label>
                    <Textarea
                      value={emailTemplates?.feedback_en?.body || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        feedback_en: { ...emailTemplates?.feedback_en, body: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20 min-h-[200px] font-mono text-sm"
                      placeholder="Dear {{guest_name}},&#10;&#10;Thank you for your visit..."
                      data-testid="textarea-feedback-en-body"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-brand-beige">Google Review URL</CardTitle>
                  <CardDescription>De link naar uw Google Reviews pagina</CardDescription>
                </CardHeader>
                <CardContent>
                  <Input
                    value={emailTemplates?.google_review_url || ""}
                    onChange={(e) => setEmailTemplates({
                      ...emailTemplates,
                      google_review_url: e.target.value
                    })}
                    className="bg-[#222222] border-white/20"
                    placeholder="https://g.page/r/..."
                    data-testid="input-google-review-url"
                  />
                </CardContent>
              </Card>

              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-green-400 text-sm">
                  <strong>Automatische verzending:</strong> Review verzoeken worden automatisch 24 uur na het bezoek verstuurd. Gasten met status "Completed" ontvangen deze email.
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="emails" className="mt-6">
            <div className="space-y-6">
              {/* Email Styling Options */}
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-brand-beige">Email Kleuren</CardTitle>
                  <CardDescription>Pas de kleuren aan van alle verstuurde emails (bevestiging, herinnering, review)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-300 font-medium">Tekstkleur</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={emailTemplates?.email_text_color || "#F4F0EA"}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            email_text_color: e.target.value
                          })}
                          className="w-12 h-10 rounded cursor-pointer"
                          data-testid="color-email-text"
                        />
                        <Input
                          value={emailTemplates?.email_text_color || "#F4F0EA"}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            email_text_color: e.target.value
                          })}
                          className="bg-[#222222] border-white/20 font-mono text-sm"
                          placeholder="#F4F0EA"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Standaard: #F4F0EA (licht beige)</p>
                    </div>
                    <div>
                      <Label className="text-gray-300 font-medium">Achtergrondkleur</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={emailTemplates?.email_background_color || "#121212"}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            email_background_color: e.target.value
                          })}
                          className="w-12 h-10 rounded cursor-pointer"
                          data-testid="color-email-bg"
                        />
                        <Input
                          value={emailTemplates?.email_background_color || "#121212"}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            email_background_color: e.target.value
                          })}
                          className="bg-[#222222] border-white/20 font-mono text-sm"
                          placeholder="#121212"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Standaard: #121212 (donker)</p>
                    </div>
                    <div>
                      <Label className="text-gray-300 font-medium">Accent kleur</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={emailTemplates?.email_accent_color || "#D4AF37"}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            email_accent_color: e.target.value
                          })}
                          className="w-12 h-10 rounded cursor-pointer"
                          data-testid="color-email-accent"
                        />
                        <Input
                          value={emailTemplates?.email_accent_color || "#D4AF37"}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            email_accent_color: e.target.value
                          })}
                          className="bg-[#222222] border-white/20 font-mono text-sm"
                          placeholder="#D4AF37"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Standaard: #D4AF37 (goud)</p>
                    </div>
                  </div>
                  {/* Preview */}
                  <div className="mt-4 p-4 rounded-lg" style={{ 
                    backgroundColor: emailTemplates?.email_background_color || "#121212",
                    border: `1px solid ${emailTemplates?.email_accent_color || "#D4AF37"}40`
                  }}>
                    <p className="text-xs text-gray-500 mb-2">Voorbeeld:</p>
                    <h3 style={{ color: emailTemplates?.email_accent_color || "#D4AF37", fontSize: '18px', marginBottom: '8px' }}>BARBAAR Middelkerke</h3>
                    <p style={{ color: emailTemplates?.email_text_color || "#F4F0EA", fontSize: '14px' }}>
                      Beste Gast,<br/><br/>
                      Uw reservering is bevestigd voor 25 december om 19:00.<br/>
                      Aantal personen: 4
                    </p>
                    <button style={{ 
                      backgroundColor: emailTemplates?.email_accent_color || "#D4AF37", 
                      color: emailTemplates?.email_background_color || "#121212",
                      padding: '8px 16px',
                      borderRadius: '4px',
                      marginTop: '12px',
                      fontWeight: 'bold',
                      border: 'none'
                    }}>Beheer Reservering</button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-brand-beige">Bevestiging Email (Nederlands)</CardTitle>
                  <CardDescription>Variabelen: guest_name, date, time, party_size, manage_url (gebruik dubbele accolades in template)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-gray-300 font-medium">Onderwerp</Label>
                    <Input
                      value={emailTemplates?.confirmation_nl?.subject || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        confirmation_nl: { ...emailTemplates?.confirmation_nl, subject: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20"
                      data-testid="input-email-nl-subject"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium">Email Body</Label>
                    <Textarea
                      value={emailTemplates?.confirmation_nl?.body || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        confirmation_nl: { ...emailTemplates?.confirmation_nl, body: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20 min-h-[200px] font-mono text-sm"
                      data-testid="textarea-email-nl-body"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-brand-beige">Confirmation Email (English)</CardTitle>
                  <CardDescription>Variables: guest_name, date, time, party_size, manage_url (use double braces in template)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-gray-300 font-medium">Subject</Label>
                    <Input
                      value={emailTemplates?.confirmation_en?.subject || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        confirmation_en: { ...emailTemplates?.confirmation_en, subject: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20"
                      data-testid="input-email-en-subject"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium">Email Body</Label>
                    <Textarea
                      value={emailTemplates?.confirmation_en?.body || ""}
                      onChange={(e) => setEmailTemplates({
                        ...emailTemplates,
                        confirmation_en: { ...emailTemplates?.confirmation_en, body: e.target.value }
                      })}
                      className="bg-[#222222] border-white/20 min-h-[200px] font-mono text-sm"
                      data-testid="textarea-email-en-body"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="shifts" className="mt-6">
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardHeader>
                <CardTitle className="text-brand-beige">Recurring Shifts</CardTitle>
                <CardDescription>Standaard shifts die automatisch toegepast worden - Bewerk capaciteit en tijden</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {recurringShifts.map((shift, index) => (
                  <div key={shift.id} className="p-4 bg-[#222222] rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Input
                        value={shift.name}
                        onChange={(e) => {
                          const updated = [...recurringShifts];
                          updated[index].name = e.target.value;
                          setRecurringShifts(updated);
                        }}
                        className="bg-transparent border-0 text-gray-300 font-semibold p-0 h-auto text-lg focus:ring-0"
                      />
                      <Switch
                        checked={shift.active}
                        onCheckedChange={(checked) => {
                          const updated = [...recurringShifts];
                          updated[index].active = checked;
                          setRecurringShifts(updated);
                        }}
                        data-testid={`switch-shift-${index}`}
                      />
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs text-gray-500">Starttijd</Label>
                        <Input
                          type="text"
                          value={shift.start_time}
                          onChange={(e) => {
                            const updated = [...recurringShifts];
                            updated[index].start_time = e.target.value;
                            setRecurringShifts(updated);
                          }}
                          placeholder="17:30"
                          className="bg-[#1A1A1A] border-white/20"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Eindtijd</Label>
                        <Input
                          type="text"
                          value={shift.end_time}
                          onChange={(e) => {
                            const updated = [...recurringShifts];
                            updated[index].end_time = e.target.value;
                            setRecurringShifts(updated);
                          }}
                          placeholder="23:00"
                          className="bg-[#1A1A1A] border-white/20"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Capaciteit/slot</Label>
                        <Input
                          type="number"
                          min="1"
                          value={shift.capacity_per_slot}
                          onChange={(e) => {
                            const updated = [...recurringShifts];
                            updated[index].capacity_per_slot = parseInt(e.target.value);
                            setRecurringShifts(updated);
                          }}
                          className="bg-[#1A1A1A] border-white/20"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Dagen</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(dayNames).map(([key, name]) => (
                            <button
                              key={key}
                              type="button"
                              onClick={() => {
                                const updated = [...recurringShifts];
                                if (updated[index].days.includes(key)) {
                                  updated[index].days = updated[index].days.filter(d => d !== key);
                                } else {
                                  updated[index].days = [...updated[index].days, key];
                                }
                                setRecurringShifts(updated);
                              }}
                              className={`px-2 py-1 text-xs rounded ${
                                shift.days.includes(key) 
                                  ? 'bg-brand-gold text-black' 
                                  : 'bg-[#1A1A1A] text-gray-500'
                              }`}
                            >
                              {name.slice(0, 2)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Beschikbare tijden (comma-separated)</Label>
                      <Input
                        value={shift.slots}
                        onChange={(e) => {
                          const updated = [...recurringShifts];
                          updated[index].slots = e.target.value;
                          setRecurringShifts(updated);
                        }}
                        placeholder="17:30,18:00,18:30,19:00..."
                        className="bg-[#1A1A1A] border-white/20 font-mono text-xs"
                        data-testid={`input-shift-slots-${index}`}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => {
                    setRecurringShifts([...recurringShifts, {
                      id: `shift-${Date.now()}`,
                      name: "Nieuwe Shift",
                      days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
                      start_time: "12:00",
                      end_time: "15:00",
                      slots: "12:00,12:30,13:00,13:30,14:00",
                      capacity_per_slot: 8,
                      active: false
                    }]);
                  }}
                  className="w-full border-dashed border-white/20 text-gray-400 hover:text-white"
                >
                  + Nieuwe Shift Toevoegen
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;